//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { TikTokConnectionStatus } from '@bvengineering/tiktok-gateway-service-types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ITikTokSettingsState,
  TIKTOK_SETTINGS_URL,
  getTikTokSettingsUrlWithError,
} from '@account-settings/redux/tiktok/tiktokSettingsModel';
import * as tiktokApi from '@apis/tiktok-settings/tiktokSettingsApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialState: ITikTokSettingsState = {
  errors: {
    fetchOauthUrlError: undefined,
    fetchSettingsError: undefined,
    connectError: undefined,
    disconnectError: undefined,
    reconnectError: undefined,
  },
  flags: {
    isFetchingOAuthUrl: false,
    isFetchingSettings: false,
    isConnecting: false,
    isDisconnecting: false,
    isReconnecting: false,
  },
  oauthEndpoint: undefined,
  isConnected: false,
  hasStaleCredentials: false,
  connectedAccount: undefined,
};

const { actions, reducer: TikTokSettingsReducer } = createSlice({
  name: SliceNames.AccountSettingsTikTok,
  initialState,
  reducers: {
    requestTikTokSettings(state) {
      state.flags.isFetchingSettings = true;
      state.errors.fetchSettingsError = undefined;
    },
    recieveTikTokSettingsSucceeded(
      state,
      action: PayloadAction<TikTokConnectionStatus>,
    ) {
      state.isConnected = action.payload.isConnected;
      state.hasStaleCredentials = action.payload.hasStaleCredentials;
      state.connectedAccount = {
        displayName: action.payload.displayName,
        scope: action.payload.scope,
      };
      state.flags.isFetchingSettings = false;
    },
    recieveTikTokSettingsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingSettings = false;
      state.errors.fetchSettingsError = action.payload;
    },
    requestTikTokOAuthEndpoint(state) {
      state.flags.isFetchingOAuthUrl = true;
      state.errors.fetchOauthUrlError = undefined;
    },
    receiveTikTokOAuthEndpointSucceeded(state, action: PayloadAction<string>) {
      state.oauthEndpoint = action.payload;
      state.flags.isFetchingOAuthUrl = false;
    },
    receiveTikTokOAuthEndpointFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingOAuthUrl = false;
      state.errors.fetchOauthUrlError = action.payload;
    },
    requestConnectTikTok(state) {
      state.flags.isConnecting = true;
      state.errors.connectError = undefined;
    },
    recieveConnectTikTokSucceeded(
      state,
      action: PayloadAction<TikTokConnectionStatus>,
    ) {
      state.isConnected = action.payload.isConnected;
      state.hasStaleCredentials = action.payload.hasStaleCredentials;
      state.connectedAccount = {
        displayName: action.payload.displayName,
        scope: action.payload.scope,
      };
      state.flags.isConnecting = false;
    },
    recieveConnectTikTokFailed(state, action: PayloadAction<string>) {
      state.flags.isConnecting = false;
      state.errors.connectError = action.payload;
    },
    requestDeAuthTikTok(state) {
      state.flags.isDisconnecting = true;
      state.errors.disconnectError = undefined;
    },
    recieveDeAuthTikTokSucceeded(state) {
      state.isConnected = false;
      state.connectedAccount = undefined;
      state.hasStaleCredentials = false;
      state.flags.isDisconnecting = false;
    },
    recieveDeAuthTikTokFailed(state, action: PayloadAction<string>) {
      state.flags.isDisconnecting = false;
      state.errors.disconnectError = action.payload;
    },
    requestReconnectTikTok(state) {
      state.flags.isReconnecting = true;
      state.errors.reconnectError = undefined;
    },
    recieveReconnectTikTokSucceeded(state, action: PayloadAction<string>) {
      state.flags.isReconnecting = false;
      state.oauthEndpoint = action.payload;
    },
    recieveReconnectTikTokFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isReconnecting = false;
      state.errors.reconnectError = action.payload;
    },
  },
});

export const getTikTokSettings = createThunk(() => async (dispatch) => {
  dispatch(actions.requestTikTokSettings());

  try {
    const response = await tiktokApi.getConnectionStatus();
    dispatch(actions.recieveTikTokSettingsSucceeded(response));
  } catch (e) {
    dispatch(
      actions.recieveTikTokSettingsFailed(getRequestErrorMessage(e) ?? ''),
    );
  }
});

export const getOauthUrl = createThunk(() => async (dispatch) => {
  dispatch(actions.requestTikTokOAuthEndpoint());

  try {
    const url = await tiktokApi.getOAuthUrl();
    dispatch(actions.receiveTikTokOAuthEndpointSucceeded(url));
  } catch (e) {
    dispatch(
      actions.receiveTikTokOAuthEndpointFailed(getRequestErrorMessage(e) ?? ''),
    );
  }
});

export const connectTikTok = createThunk(
  (state: string, authCode: string) => async (dispatch) => {
    dispatch(actions.requestConnectTikTok());

    try {
      const connectionStatus = await tiktokApi.connect({ state, authCode });
      dispatch(actions.recieveConnectTikTokSucceeded(connectionStatus));
      // Redirect to settings after connected
      window.location.replace(TIKTOK_SETTINGS_URL);
    } catch (e) {
      const errorMessage = getRequestErrorMessage(e);
      dispatch(actions.recieveConnectTikTokFailed(errorMessage ?? ''));

      // If an error occurs we redirect to settings with an error code query param
      window.location.replace(getTikTokSettingsUrlWithError('0'));
    }
  },
);

export const disconnectTikTok = createThunk(() => async (dispatch) => {
  dispatch(actions.requestDeAuthTikTok());

  try {
    await tiktokApi.revokeToken();
    dispatch(actions.recieveDeAuthTikTokSucceeded());

    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'TikTok account successfully disconnected!',
        type: ToastType.SUCCESS,
      }),
    );
  } catch (e) {
    const errorMessage = getRequestErrorMessage(e);
    dispatch(actions.recieveDeAuthTikTokFailed(errorMessage ?? ''));
    dispatch(
      toastNotificationsActions.addToastNotification({
        title:
          'An error occurred when trying to disconnect your TikTok account.',
        type: ToastType.ERROR,
      }),
    );
  }
});

export const reconnectTikTok = createThunk(() => async (dispatch) => {
  dispatch(actions.requestReconnectTikTok());

  try {
    await tiktokApi.revokeToken();
    const url = await tiktokApi.getOAuthUrl();

    dispatch(actions.recieveReconnectTikTokSucceeded(url));
  } catch (e) {
    const errorMessage = getRequestErrorMessage(e);
    dispatch(actions.recieveReconnectTikTokFailed(errorMessage));

    dispatch(
      toastNotificationsActions.addToastNotification({
        title:
          errorMessage ??
          'An error occurred when trying to reconnect your TikTok account.',
        type: ToastType.ERROR,
      }),
    );
  }
});

export default TikTokSettingsReducer;
